import React, {Suspense, lazy, useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    alpha,
    AppBar,
    Box,
    Button,
    Container,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LoginIcon from '@mui/icons-material/Login';
import {Link as RouterLink, Outlet, useLocation} from "react-router-dom";
import harLogo from "../images/logo.png";
import QuoteStepperNav from "../pages/QuoteStepperNav";
import CallNowButton from "../components/shared/CallNowButton";
import { getAppBarSurface } from "../utils/getAppBarSurface";

const Footer = lazy(() => import("../components/shared/Footer"));

const navLinks = [
    {label: 'Home', to: '/', icon: <HomeIcon/>},
    {label: 'About us', to: '/about', icon: <InfoIcon/>},
    {label: 'Services', to: '/services', icon: <BuildIcon/>},
    {label: 'Fleet', to: '/fleet', icon: <DirectionsCarIcon/>},
    {label: 'Contact us', to: '/contact', icon: <ContactMailIcon/>},
    {label: 'Login', to: '/login', icon: <LoginIcon/>},
];

const Logo = () => (
    <Box
        component={RouterLink}
        to="/"
        display="flex"
        alignItems="center"
        mr={4}
        sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
                opacity: 0.8,
            },
            transition: 'opacity 0.2s ease',
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: {xs: 44, sm: 52},
                height: {xs: 44, sm: 52},
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                overflow: 'hidden',
                flexShrink: 0
            }}
        >
            <Box
                component="img"
                src={harLogo}
                alt="H-Auto Repair Logo"
                sx={{
                    height: {xs: 32, sm: 40},
                    width: 'auto',
                    objectFit: 'contain'
                }}
            />
        </Box>
    </Box>
);

const AppBarLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleDrawerToggle = useCallback(() => {
        setDrawerOpen(prev => !prev);
    }, []);

    const handleLinkClick = useCallback(() => {
        // Close drawer immediately when link is clicked (don't wait for navigation)
        setDrawerOpen(false);
    }, []);

    const isQuoteFlow = location.pathname.startsWith("/quote");
    const isQuoteStartPage = location.pathname === "/quote";
    const isHomePage = location.pathname === '/';
    const isQuoteStepPage = isQuoteFlow && !isQuoteStartPage; // Quote flow but not the start page
    const isAuthPage = location.pathname === "/login" || location.pathname === "/register" ||
        location.pathname === "/forgot-password" || location.pathname === "/reset-password";

    // Determine current step index for quote flow (updated to match new diagnostic flow)
    const quoteStepPaths = [
        "/quote/vehicle",   // 0
        "/quote/services",  // 1
        "/quote/contact",   // 2
        "/quote/location",  // 3
        "/quote/schedule",  // 4
        "/quote/book"       // 5
    ];
    const currentStep = location.pathname === "/quote"
        ? -1
        : quoteStepPaths.findIndex(path => location.pathname === path);

    // Memoize current pathname to avoid re-renders
    const currentPath = useMemo(() => location.pathname, [location.pathname]);

    const outletRef = useRef(null);

    // Reset scroll synchronously before paint on every route change
    useLayoutEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const root = document.getElementById('root');
        if (root) root.scrollTop = 0;
        let node = outletRef.current;
        while (node && node !== document.body) {
            if (node.scrollTop > 0) node.scrollTop = 0;
            node = node.parentElement;
        }
    }, [currentPath]);

    const appBarSurface = useMemo(() => getAppBarSurface(theme), [theme]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                // Quote steps: margin-top on the outlet Container is outside its box — without this, body
                // background.default (#f8fafc) shows as a gray band below the stepper and at the top.
                ...(isQuoteStepPage && {
                    bgcolor: 'background.paper',
                    height: '100vh',
                    maxHeight: '100vh',
                }),
            }}
        >
            <AppBar position="fixed" sx={{
                background: appBarSurface,
                boxShadow: 2,
                zIndex: (theme) => theme.zIndex.appBar,
                borderRadius: 0
            }}>
                <Toolbar sx={{
                    justifyContent: 'space-between',
                    minHeight: { xs: isQuoteStepPage && isMobile ? 52 : 56, sm: 64 },
                    py: isQuoteStepPage && isMobile ? 0.5 : 0,
                    px: {xs: 1, sm: 2, md: 4}
                }}>
                    {/* Logo */}
                    <Logo/>
                    {/* Navigation and Get Quote Button (always visible) */}
                    {isMobile ? (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {isQuoteFlow ? (
                                    <CallNowButton
                                        variant="contained"
                                        size="medium"
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                                            color: 'white',
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            px: 4,
                                            py: 0.75,
                                            fontSize: '0.9375rem',
                                            textTransform: 'none',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            border: 'none',
                                            '@media (hover: hover)': {
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                                                    boxShadow: `0 6px 16px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`,
                                                    transform: 'translateY(-2px)',
                                                },
                                            },
                                            '@media (hover: none)': {
                                                '&:active': { transform: 'scale(0.97)' },
                                            },
                                        }}
                                    />
                                ) : (
                                    <Button
                                        variant="contained"
                                        component={RouterLink}
                                        to="/quote"
                                        disableRipple
                                        onTouchEnd={(e) => {
                                            setTimeout(() => {
                                                if (e.currentTarget) e.currentTarget.blur();
                                            }, 0);
                                        }}
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                                            color: 'white',
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            px: 4,
                                            py: 0.75,
                                            fontSize: '0.9375rem',
                                            textTransform: 'none',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            userSelect: 'none',
                                            transition: 'all 0.2s ease',
                                            '@media (hover: hover)': {
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                                                    boxShadow: `0 6px 16px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`,
                                                    transform: 'translateY(-2px)',
                                                },
                                            },
                                            '@media (hover: none)': {
                                                '&:active': { transform: 'scale(0.97)' },
                                            },
                                            '&:focus-visible': {
                                                outline: `2px solid ${theme.palette.warning?.dark || '#d97706'}`,
                                                outlineOffset: 2,
                                            },
                                        }}
                                    >
                                        Get Quote
                                    </Button>
                                )}
                                <IconButton
                                    edge="end"
                                    color="inherit"
                                    onClick={handleDrawerToggle}
                                    sx={{
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            }
                                        }
                                    }}
                                >
                                    <MenuIcon/>
                                </IconButton>
                            </Stack>
                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={handleDrawerToggle}
                                transitionDuration={250}
                                ModalProps={{
                                    keepMounted: false,
                                    disableScrollLock: isAuthPage,
                                }}
                                sx={{
                                    zIndex: (theme) => theme.zIndex.drawer + 1,
                                }}
                                PaperProps={{
                                    sx: {
                                        width: {xs: '85%', sm: 320},
                                        backgroundColor: theme.palette.background.paper,
                                        boxShadow: `-4px 0 24px ${alpha(theme.palette.common.black, 0.15)}`,
                                        zIndex: (theme) => theme.zIndex.drawer + 1,
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {/* Header */}
                                    <Box
                                        sx={{
                                            p: 3,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.info.main})`,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'white',
                                                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                                                    overflow: 'hidden',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={harLogo}
                                                    alt="H-Auto Repair Logo"
                                                    sx={{height: 32, width: 'auto', objectFit: 'contain'}}
                                                />
                                            </Box>
                                            <Typography variant="h6" sx={{fontWeight: 700, fontSize: '1.125rem'}}>
                                                Menu
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            onClick={handleDrawerToggle}
                                            sx={{
                                                color: 'white',
                                                '@media (hover: hover)': {
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                                                    }
                                                }
                                            }}
                                        >
                                            <CloseIcon/>
                                        </IconButton>
                                    </Box>

                                    {/* Navigation Links */}
                                    <List sx={{flex: 1, pt: 2, px: 1}}>
                                        {navLinks.map((link) => {
                                            const isActive = currentPath === link.to;
                                            return (
                                                <ListItem
                                                    key={link.label}
                                                    component={RouterLink}
                                                    to={link.to}
                                                    onClick={handleLinkClick}
                                                    sx={{
                                                        mb: 0.5,
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1.5,
                                                        backgroundColor: isActive
                                                            ? alpha(theme.palette.primary.main, 0.1)
                                                            : 'transparent',
                                                        '@media (hover: hover)': {
                                                            '&:hover': {
                                                                backgroundColor: isActive
                                                                    ? alpha(theme.palette.primary.main, 0.15)
                                                                    : alpha(theme.palette.primary.main, 0.08),
                                                            }
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: 40,
                                                            color: isActive
                                                                ? theme.palette.primary.main
                                                                : theme.palette.text.primary,
                                                        }}
                                                    >
                                                        {link.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={link.label}
                                                        primaryTypographyProps={{
                                                            fontWeight: 500,
                                                            fontSize: '1rem',
                                                            color: isActive
                                                                ? theme.palette.primary.main
                                                                : theme.palette.text.primary,
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>

                                    {/* Get Quote Button at Bottom */}
                                    <Box sx={{p: 2, pt: 1}}>
                                        <Divider sx={{mb: 2}}/>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            component={RouterLink}
                                            to="/quote"
                                            onClick={handleLinkClick}
                                        sx={{
                                            background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                                            color: 'white',
                                            borderRadius: 2,
                                            py: 1.5,
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                            transition: 'all 0.2s ease',
                                            '@media (hover: hover)': {
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                                                    boxShadow: `0 6px 16px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`,
                                                    transform: 'translateY(-2px)',
                                                }
                                            },
                                            '@media (hover: none)': {
                                                '&:active': {
                                                    transform: 'scale(0.97)',
                                                    boxShadow: `0 2px 8px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`
                                                }
                                            }
                                        }}
                                        >
                                            Get Instant Quote
                                        </Button>
                                    </Box>
                                </Box>
                            </Drawer>
                        </>
                    ) : (
                        <Stack direction="row" spacing={isTablet ? 1 : 3} alignItems="center">
                            {navLinks.map((link) => {
                                const isActive = currentPath === link.to;
                                return (
                                    <Button
                                        key={link.label}
                                        {...(isActive ? { component: 'div' } : { component: RouterLink, to: link.to })}
                                        sx={{
                                            color: isActive ? theme.palette.primary.main : (theme.palette.dark?.text || '#fff'),
                                            fontWeight: theme.typography.subtitle1.fontWeight,
                                            fontSize: isTablet ? theme.typography.body2.fontSize : theme.typography.body1.fontSize,
                                            textTransform: 'none',
                                            px: isTablet ? 1 : 2,
                                            minWidth: 0,
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                            '@media (hover: hover)': {
                                                '&:hover': {
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                },
                                            },
                                        }}
                                        disableRipple={isActive}
                                    >
                                        {link.label}
                                    </Button>
                                );
                            })}
                            {isQuoteFlow ? (
                                <CallNowButton
                                    variant="contained"
                                    sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                                        color: 'white',
                                        borderRadius: 999,
                                        fontWeight: 600,
                                        px: isTablet ? 3.5 : 5,
                                        py: isTablet ? 0.875 : 1.125,
                                        fontSize: isTablet ? '0.9375rem' : '1rem',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                        textTransform: 'none',
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                                                boxShadow: `0 6px 20px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`,
                                                transform: 'translateY(-2px)',
                                            },
                                        },
                                        '&:active': {
                                            transform: 'scale(0.98)',
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                        },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.warning?.dark || '#d97706'}`,
                                            outlineOffset: 2,
                                        },
                                    }}
                                />
                            ) : (
                                <Button
                                    variant="contained"
                                    component={RouterLink}
                                    to="/quote"
                                    disableRipple
                                    sx={{
                                        background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'}, ${theme.palette.warning?.dark || '#d97706'})`,
                                        color: 'white',
                                        borderRadius: 999,
                                        fontWeight: 600,
                                        px: isTablet ? 3.5 : 5,
                                        py: isTablet ? 0.875 : 1.125,
                                        fontSize: isTablet ? '0.9375rem' : '1rem',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                        textTransform: 'none',
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'}, ${theme.palette.warning?.main || '#f59e0b'})`,
                                                boxShadow: `0 6px 20px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.5)}`,
                                                transform: 'translateY(-2px)',
                                            },
                                        },
                                        '&:active': {
                                            transform: 'scale(0.98)',
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}`,
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                        },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme.palette.warning?.dark || '#d97706'}`,
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    Get Quote
                                </Button>
                            )}
                        </Stack>
                    )}
                </Toolbar>
            </AppBar>
            {/* Stepper for quote flow steps (not on initial /quote page), styled to match AppBar */}
            {isQuoteStepPage && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: { xs: isQuoteStepPage && isMobile ? 52 : 56, sm: 64 },
                        left: 0,
                        right: 0,
                        zIndex: (theme) => theme.zIndex.drawer,
                        background: appBarSurface,
                    }}
                >
                    <QuoteStepperNav currentStep={currentStep}/>
                </Box>
            )}
            {/* ✅ This renders the actual page like Home, About, etc. */}
            <Container
                ref={outletRef}
                maxWidth={false}
                disableGutters={isQuoteStepPage || isAuthPage || isQuoteStartPage}
                sx={isQuoteStepPage ? {
                    position: 'fixed',
                    top: { xs: 108, sm: 152 },
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    zIndex: 0,
                } : {
                    mt: isAuthPage ? 0 : {xs: 7, sm: 8},
                    pt: isAuthPage ? 0 : isHomePage ? 0 : isQuoteStartPage ? 0 : 2,
                    px: isAuthPage || isQuoteStartPage ? 0 : undefined,
                    flex: 1,
                }}
            >
                <Outlet key={currentPath} />
            </Container>
            {/* Footer - Hide on quote pages and auth pages (auth pages have their own AuthFooter) */}
            {!isQuoteFlow && !isAuthPage && (
                <Suspense fallback={null}>
                    <Footer/>
                </Suspense>
            )}
        </Box>
    );
};

export default AppBarLayout;



